/**
 * iVisora JSON parser — the first implementation of the parser contract.
 *
 * Four shapes reach this parser and all four describe the same thing. They
 * exist because the document has been written by hand, produced by the AI
 * generator, exported by `buildTemplateDoc`, and typed by an LLM against a
 * pasted schema — each with its own idea of where `pages` lives. Detection is
 * table-driven and ordered, because "v1 read as bare" is the failure mode here
 * and it is silent.
 */

import { emptyAssets, emptyReport } from '../types';
import { PARSE_STAGES } from '../constants';

/** Top-level keys the document shape recognises. Anything else earns a warning. */
const KNOWN_TOP_LEVEL_KEYS = new Set([
  'name', 'description', 'library_id', 'template_json', 'pages', 'elements',
  'canvas_width', 'canvas_height', 'canvas_background', 'tags', 'is_premium',
  'source', 'status', 'version', 'assets', 'variables', 'metadata',
  'thumbnail', 'category_id',
]);

/** Matches the backend's TemplateBase default, so a silent fallback is not a surprise. */
const DEFAULT_CANVAS = 1080;

/**
 * First usable canvas dimension, or the default.
 *
 * Zero is rejected rather than accepted: `Number.isFinite(0)` is true and the
 * validator would pass it, so a document declaring `canvas_width: 0` would
 * import cleanly and produce a template with no width. A zero here always
 * means "unset", never "deliberately nothing".
 */
const firstNumber = (...candidates) => {
  const hit = candidates.find((v) => Number.isFinite(v) && v > 0);
  return hit === undefined ? DEFAULT_CANVAS : hit;
};

/**
 * Which of the four shapes is this?
 *
 * Order is normative: v2 before v1 (v2 is a v1 that also declares a version),
 * and both before bare (a document with `template_json` may also carry a
 * top-level `pages` left over from an older export).
 */
const detectShape = (raw) => {
  const tj = raw.template_json;
  if (tj && Array.isArray(tj.pages)) return tj.version === 2 ? 'v2' : 'v1';
  if (Array.isArray(raw.pages)) return 'bare';
  if (Array.isArray(raw.elements)) return 'legacy';
  return 'unknown';
};

const pagesFor = (raw, shape) => {
  if (shape === 'v2' || shape === 'v1') return raw.template_json.pages;
  if (shape === 'bare') return raw.pages;
  if (shape === 'legacy') {
    // A bare element array is one page by definition — it predates multi-page.
    return [{
      id: 'page-1',
      name: 'Page 1',
      background: typeof raw.canvas_background === 'string' ? raw.canvas_background : '#ffffff',
      elements: raw.elements,
    }];
  }
  return [];
};

const canParse = (input) => {
  if (!input) return false;
  if (input.filename && input.filename.toLowerCase().endsWith('.json')) return true;
  if (input.mimeType && input.mimeType.includes('json')) return true;
  return typeof input.text === 'string' && input.text.trimStart().startsWith('{');
};

/**
 * The importer hands its own cumulative pipeline fraction to onProgress, and
 * reserves 0.2–0.5 for the parse stage. A parser reporting raw 0→1 would make
 * a progress bar jump to full and then back down. Five future parsers will be
 * written by reading this one, so the band is respected here.
 */
const PARSE_BAND = { start: 0.2, end: 0.5 };

const parse = async (input, context = {}) => {
  const { onProgress = () => {} } = context;
  onProgress(PARSE_STAGES.PARSE, PARSE_BAND.start);

  const report = emptyReport();

  let raw;
  try {
    raw = JSON.parse(input.text);
  } catch (err) {
    report.errors.push(`Invalid JSON: ${err.message}`);
    return {
      document: { canvas_width: DEFAULT_CANVAS, canvas_height: DEFAULT_CANVAS, pages: [] },
      assets: emptyAssets(),
      report,
    };
  }

  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    report.errors.push('Template JSON must be an object');
    return {
      document: { canvas_width: DEFAULT_CANVAS, canvas_height: DEFAULT_CANVAS, pages: [] },
      assets: emptyAssets(),
      report,
    };
  }

  const shape = detectShape(raw);
  if (shape === 'unknown') {
    report.errors.push('Template JSON has no pages[] and no elements[] — nothing to import');
  } else {
    report.supported.push(`Read as ${shape} shape`);
  }

  const tj = raw.template_json || {};
  const pages = pagesFor(raw, shape);

  Object.keys(raw).forEach((key) => {
    if (!KNOWN_TOP_LEVEL_KEYS.has(key)) {
      report.warnings.push(`Unrecognized top-level key "${key}" — it was not imported`);
    }
  });

  // v2 may declare assets and variables. This phase records that they were seen
  // rather than acting on them: consuming them is Phase 4's format work, and
  // silently dropping them would leave the author wondering where they went.
  if (shape === 'v2') {
    if (tj.assets) report.partial.push('v2 assets block detected — not consumed in this version');
    if (tj.variables) report.partial.push('v2 variables block detected — placeholders are re-detected from content');
  }

  const document = {
    name: typeof raw.name === 'string' ? raw.name : '',
    description: typeof raw.description === 'string' ? raw.description : '',
    library_id: typeof raw.library_id === 'string' ? raw.library_id : '',
    tags: Array.isArray(raw.tags) ? raw.tags : [],
    is_premium: raw.is_premium === true,
    canvas_width: firstNumber(tj.canvas_width, raw.canvas_width),
    canvas_height: firstNumber(tj.canvas_height, raw.canvas_height),
    canvas_background:
      typeof raw.canvas_background === 'string' ? raw.canvas_background : '#ffffff',
    pages: Array.isArray(pages) ? pages : [],
  };

  if (!document.name) report.warnings.push('Template has no name — it will import as Untitled');

  onProgress(PARSE_STAGES.PARSE, PARSE_BAND.end);
  return { document, assets: emptyAssets(), report };
};

/** @type {import('../types').Parser} */
export const jsonParser = {
  id: 'json',
  label: 'iVisora JSON',
  priority: 10,
  accept: ['.json', 'application/json'],
  canParse,
  parse,
};
